import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Pencil, Play, Calendar as CalIcon, Mail, Plus } from "lucide-react";
import Topbar from "@/components/layout/Topbar";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";

const FREQ_LABELS = { daily: "Daily", weekly: "Weekly", monthly: "Monthly" };

export default function Reports() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const runNow = async (id) => {
    try {
      await api.post(`/my-dashboards/${id}/run`);
      toast.success("Generated — check Inbox");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const formatSchedule = (s) => {
    if (!s) return "Manual only";
    const t = `${String(s.hour).padStart(2, "0")}:${String(s.minute).padStart(2, "0")} UTC`;
    if (s.frequency === "daily") return `Daily · ${t}`;
    if (s.frequency === "weekly") {
      const d = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][s.day_of_week ?? 1];
      return `Weekly · ${d} · ${t}`;
    }
    if (s.frequency === "monthly") return `Monthly · day ${s.day_of_month} · ${t}`;
    return s.frequency;
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      <Topbar
        title="Saved Reports"
        subtitle="Build, schedule, and download your custom reports"
        right={
          <Link
            to="/dashboards/builder"
            className="flex h-9 items-center gap-1.5 rounded-sm bg-brand-orange px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#C1701C]"
          >
            <Plus className="h-3.5 w-3.5" />
            New report
          </Link>
        }
      />
      <div className="p-6 animate-fade-in" data-testid="reports-page">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="rounded-md border border-dashed border-slate-200 bg-white p-12 text-center" data-testid="reports-empty">
            <FileText className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">
              No reports yet. Build a dashboard and check "Save as Report" to schedule it.
            </p>
            <Link
              to="/dashboards/builder"
              className="mt-3 inline-flex items-center gap-2 rounded-sm bg-brand-orange px-3 py-2 text-xs font-semibold uppercase tracking-widest text-white"
            >
              <Plus className="h-3.5 w-3.5" />
              Create report
            </Link>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3 text-left">Report</th>
                  <th className="px-5 py-3 text-left">Schedule</th>
                  <th className="px-5 py-3 text-left">Formats</th>
                  <th className="px-5 py-3 text-left">Recipients</th>
                  <th className="px-5 py-3 text-left">Owner</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr
                    key={r.dashboard_id}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                    data-testid={`report-row-${r.dashboard_id}`}
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-brand-orange text-white">
                          <FileText className="h-3.5 w-3.5" />
                        </div>
                        <div>
                          <div className="font-medium text-navy-600">{r.name}</div>
                          <div className="text-[10px] text-slate-500">{r.widgets?.length || 0} widgets</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-600">
                      <div className="flex items-center gap-1.5">
                        <CalIcon className="h-3 w-3 text-slate-400" />
                        {formatSchedule(r.schedule)}
                      </div>
                      {r.schedule?.next_run_at && (
                        <div className="mt-0.5 text-[10px] text-slate-400">
                          next: {new Date(r.schedule.next_run_at).toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1">
                        {(r.output_formats || []).map((f) => (
                          <span key={f} className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-slate-600">
                            {f}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-600">
                      {(r.email_recipients || []).length === 0 ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Mail className="h-3 w-3 text-slate-400" />
                          {r.email_recipients.length} recipient{r.email_recipients.length !== 1 && "s"}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-500">{r.owner_name || r.owner_email}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => runNow(r.dashboard_id)}
                          data-testid={`run-${r.dashboard_id}`}
                          className="flex h-8 items-center gap-1 rounded-sm bg-brand-orange px-2 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#C1701C]"
                        >
                          <Play className="h-3 w-3" />
                          Run
                        </button>
                        {r.is_owner && (
                          <Link
                            to={`/dashboards/builder/${r.dashboard_id}`}
                            data-testid={`edit-report-${r.dashboard_id}`}
                            className="rounded-sm p-1.5 text-slate-400 hover:bg-slate-100 hover:text-navy-600"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
